import type {NumberLike, TemplateLikeNode, MaterialLikeNode} from "@cm/template-nodes"
import {Mattress} from "#mattress/nodes/mattress"
import type {MattressUVControlPoint} from "#mattress/nodes/curve/mattress-curve"
import {MattressCurve} from "#mattress/nodes/curve/mattress-curve"
import {MattressCut} from "#mattress/nodes/mattress-cut"
import type {MattressFabricLayout} from "#mattress/nodes/mattress-cut"
import {MattressReferenceFrame} from "#mattress/nodes/reference-frame/mattress-reference-frame"
import type {MattressGlobalReferenceFrame} from "#mattress/nodes/reference-frame/mattress-reference-frame"
import {MattressComponentGraph} from "#mattress/nodes/component/mattress-component-graph"
import {MattressMesh} from "#mattress/nodes/mattress-mesh"
import type {MattressMeshGeometryParameters} from "#mattress/nodes/mattress-mesh"
import {MattressComponentInstance} from "#mattress/nodes/component/mattress-component-instance"
import {MattressNodes} from "#mattress/nodes/mattress-nodes"
import type {MattressComponentGraphNode} from "#mattress/nodes/mattress-nodes"
import type {MattressComponentInstanceParametersParameters} from "#mattress/nodes/component/mattress-component-instance"
import type {MattressMeshGeometryParametersData} from "#mattress/nodes/component/mattress-component-types"
import type {MattressComponentGraphLikeNode, MattressCurveLikeNode, MattressReferenceFrameLikeNode} from "#mattress/role-markers"
import {MattressCurveInput} from "#mattress/nodes/curve/mattress-curve-input"
import {MattressReferenceFrameInput} from "#mattress/nodes/reference-frame/mattress-reference-frame-input"
import {MattressComponentGraphGenerator} from "#mattress/nodes/component/mattress-component-graph-generator"
import type {MattressComponentGraphGeneratorUnitRepeat1DParameters} from "#mattress/nodes/component/mattress-component-graph-generator"
import {MattressComputedCurve} from "#mattress/nodes/curve/mattress-computed-curve"
import type {MattressComputedCurveSwapReferenceFrameParameters} from "#mattress/nodes/curve/mattress-computed-curve"
import {MattressMaterialAssignment} from "#mattress/nodes/mattress-material-assignment"
import {MattressCurveExport} from "#mattress/nodes/curve/mattress-curve-export"
import {MattressGroup} from "#mattress/nodes/mattress-group"

import {Matrix4} from "@cm/math"

export function createMattressReferenceFrame(
    name: string,
    type: "xAxisRing" | "yAxisRing" | "zAxisRing" = "zAxisRing",
    parent: MattressReferenceFrameLikeNode | MattressGlobalReferenceFrame = "global",
    origin: NumberLike | null = null,
    unit: NumberLike | null = null,
) {
    return new MattressReferenceFrame({parameters: {name, type, parent, origin, unit}})
}

export function createCurveControlPointsForHorizontalLoop(size: number): MattressUVControlPoint[] {
    const cornerInset = 0.6
    const isCorner = true
    const isSectionBreak = false
    const s = size
    const c = size * cornerInset
    return [
        {corner: isCorner, sectionBreak: isSectionBreak, uv: [-s, -s], uvSpace: "global"},
        {corner: isCorner, sectionBreak: isSectionBreak, uv: [-c, -s], uvSpace: "global"},
        {corner: isCorner, sectionBreak: isSectionBreak, uv: [c, -s], uvSpace: "global"},
        {corner: isCorner, sectionBreak: isSectionBreak, uv: [s, -s], uvSpace: "global"},
        {corner: isCorner, sectionBreak: isSectionBreak, uv: [s, -c], uvSpace: "global"},
        {corner: isCorner, sectionBreak: isSectionBreak, uv: [s, c], uvSpace: "global"},
        {corner: isCorner, sectionBreak: isSectionBreak, uv: [s, s], uvSpace: "global"},
        {corner: isCorner, sectionBreak: isSectionBreak, uv: [c, s], uvSpace: "global"},
        {corner: isCorner, sectionBreak: isSectionBreak, uv: [-c, s], uvSpace: "global"},
        {corner: isCorner, sectionBreak: isSectionBreak, uv: [-s, s], uvSpace: "global"},
        {corner: isCorner, sectionBreak: isSectionBreak, uv: [-s, c], uvSpace: "global"},
        {corner: isCorner, sectionBreak: isSectionBreak, uv: [-s, -c], uvSpace: "global"},
    ]
}

export function createMattressCurveGlobal(name: string, size: number) {
    return new MattressCurve({
        parameters: {
            name,
            closed: true,
            resolution: 2,
            controlPoints: createCurveControlPointsForHorizontalLoop(size),
        },
    })
}

export function createMattressCurve(name: string, vCoord: number, referenceFrame: MattressReferenceFrameLikeNode, pointsPerCorner: number = 3) {
    const kEps = 0.001
    const vCoord_ = vCoord + kEps * 10

    if (pointsPerCorner < 3) throw new Error("pointsPerCorner must be at least 3")

    const cornersU = [-0.25, 0.25, 0.75, -0.75]
    const pointsPerCornerU = Array.from({length: pointsPerCorner}, (_, i) => {
        const u = -0.05 + (i / (pointsPerCorner - 1)) * 0.1
        return u >= 0 ? u - kEps : u + kEps
    })

    const pointsU = cornersU
        .map((cornerU) => {
            return pointsPerCornerU.map((offset) => cornerU + offset)
        })
        .flat()

    return new MattressCurve({
        parameters: {
            name,
            closed: true,
            resolution: 1,
            controlPoints: pointsU.map((u) => ({
                corner: true,
                sectionBreak: false,
                uv: [u, vCoord_],
                uvSpace: referenceFrame,
            })),
        },
    })
}

export function createMattressCurveDefault(name: string) {
    return new MattressCurve({
        parameters: {
            name,
            closed: true,
            resolution: 2,
            controlPoints: [
                {corner: true, sectionBreak: false, uv: [-1, 0], uvSpace: "global"},
                {corner: true, sectionBreak: false, uv: [1, 0], uvSpace: "global"},
            ],
        },
    })
}

export function createMattressCut(
    name: string,
    conditions: {curve: MattressCurveLikeNode; side: "left" | "right"}[],
    fabricLayout: MattressFabricLayout = "Waterfall",
    material: MaterialLikeNode | null = null,
) {
    return new MattressCut({
        parameters: {
            name,
            conditions,
            materialAssignment: material ? new MattressMaterialAssignment({parameters: {node: material, side: "front"}}) : null,
            fabricLayout,
        },
    })
}

export function createMattressMesh(name: string = "Mattress Mesh", params: Partial<MattressMeshGeometryParameters> = {}) {
    return new MattressMesh({
        parameters: {
            name,
            geometry: {
                width: params.width ?? 140,
                length: params.length ?? 200,
                height: params.height ?? 25,
                cornerRadius: params.cornerRadius ?? 10,
                topRadius: params.topRadius ?? 5,
                bottomRadius: params.bottomRadius ?? 5,
                cornerRadiusSegments: params.cornerRadiusSegments ?? 10,
                topRadiusSegments: params.topRadiusSegments ?? 10,
                bottomRadiusSegments: params.bottomRadiusSegments ?? 10,
                widthSegments: params.widthSegments ?? 10,
                heightSegments: params.heightSegments ?? 10,
                lengthSegments: params.lengthSegments ?? 10,
                resolutionMultiplier: params.resolutionMultiplier ?? 1,
            },
        },
    })
}

export function createMattressComponentGraph(name: string, nodes: MattressComponentGraphNode[]) {
    return new MattressComponentGraph({
        parameters: {
            name,
            nodes: new MattressNodes({parameters: {list: nodes}}),
        },
    })
}

export function createMattressComponentInstance(
    name: string,
    id: string,
    graph: MattressComponentGraphLikeNode | TemplateLikeNode | null,
    parameters: MattressComponentInstanceParametersParameters = {},
) {
    return new MattressComponentInstance({parameters: {name, id, graph, parameters}})
}

export function createMattressCurveInput(name: string, id: string, defaultValue: MattressCurve | undefined = undefined) {
    return new MattressCurveInput({parameters: {name, id, default: defaultValue}})
}

export function createMattressCurveExport(name: string, id: string, node: MattressCurveLikeNode) {
    return new MattressCurveExport({parameters: {name, id, node}})
}

export function createMattressReferenceFrameInput(name: string, id: string, defaultValue: MattressReferenceFrame | undefined = undefined) {
    return new MattressReferenceFrameInput({parameters: {name, id, default: defaultValue}})
}

export function createMattressComponnentGraphGenerator(name: string, params: MattressComponentGraphGeneratorUnitRepeat1DParameters) {
    if (params.type === "MattressComponentGraphGeneratorUnitRepeat1D") {
        return new MattressComponentGraphGenerator({parameters: {name, parameters: params}})
    } else throw new Error(`Unsupported MattressComponentGraphGenerator type: ${params.type}`)
}

export function createMattressComputedCurve(name: string, params: MattressComputedCurveSwapReferenceFrameParameters) {
    return new MattressComputedCurve({parameters: {name, parameters: params}})
}

export function createMattressGroup(name: string, children: MattressComponentGraphNode[]) {
    return new MattressGroup({
        parameters: {
            name,
            nodes: new MattressNodes({parameters: {list: children}}),
        },
    })
}

export function createMattress(rootGraphNodes?: MattressComponentGraphNode[]) {
    return new Mattress({
        parameters: {
            name: "Mattress",
            root: createMattressComponentGraph("root", rootGraphNodes ? rootGraphNodes : [createMattressMesh("Base Mesh")]),
            visible: true,
            lockedTransform: Matrix4.identity().toArray(),
        },
    })
}

export type CreateMattressCurveForLoopOption =
    | {
          type: "side"
          height: number
          controlPointsPerCorner: number
      }
    | {
          type: "top"
          distanceFromBorder: number
          controlPointsPerCorner: number
      }
    | {
          type: "bottom"
          distanceFromBorder: number
          controlPointsPerCorner: number
      }

export function createMattressCurveControlPointsForLoopOption(
    option: CreateMattressCurveForLoopOption,
    mattressMeshGeometryParameters: MattressMeshGeometryParametersData,
    getCanonicalUVForPoint: (point: [number, number, number]) => [number, number],
) {
    if (option.controlPointsPerCorner < 3) throw new Error("controlPointsPerCorner must be at least 3")

    const {width, length, height, topRadius, bottomRadius, cornerRadius} = mattressMeshGeometryParameters

    const angleStepPerCorner = Math.PI / 2 / (option.controlPointsPerCorner - 1)
    const anglesPerCorner = Array.from({length: option.controlPointsPerCorner}, (_, i) => i * angleStepPerCorner)

    const loopCorners = [
        {angleOffset: 0, cornerCenter: {x: length / 2 - cornerRadius, z: width / 2 - cornerRadius}},
        {angleOffset: Math.PI / 2, cornerCenter: {x: -length / 2 + cornerRadius, z: width / 2 - cornerRadius}},
        {angleOffset: Math.PI, cornerCenter: {x: -length / 2 + cornerRadius, z: -width / 2 + cornerRadius}},
        {angleOffset: -Math.PI / 2, cornerCenter: {x: length / 2 - cornerRadius, z: -width / 2 + cornerRadius}},
    ]

    const generateLoopAtRadius = (radius: number) =>
        loopCorners.flatMap(({angleOffset, cornerCenter}) =>
            anglesPerCorner.map((angle) => {
                const angleWithOffset = angle + angleOffset
                return {x: cornerCenter.x + Math.cos(angleWithOffset) * radius, y: 0, z: cornerCenter.z + Math.sin(angleWithOffset) * radius}
            }),
        )

    const loopPointsBase = generateLoopAtRadius(cornerRadius)

    const controlPoints: MattressUVControlPoint[] = (() => {
        if (option.type === "side") {
            if (option.height > height) throw new Error("Height cannot be greater than mattress height")
            let effectiveRadius = cornerRadius
            if (option.height < bottomRadius) {
                effectiveRadius = cornerRadius - bottomRadius + Math.sqrt(bottomRadius ** 2 - (bottomRadius - option.height) ** 2)
            } else if (option.height > height - topRadius) {
                effectiveRadius = cornerRadius - topRadius + Math.sqrt(topRadius ** 2 - (option.height - (height - topRadius)) ** 2)
            }

            return generateLoopAtRadius(effectiveRadius).map((point) => ({...point, y: option.height}))
        } else if (option.type === "top" || option.type === "bottom") {
            const distanceFromBorder = option.distanceFromBorder
            if (distanceFromBorder < 0) throw new Error("Distance from border cannot be negative")
            if (distanceFromBorder > Math.min(width, length) / 2) throw new Error("Distance from border cannot be greater than half of width or length")
            const y = option.type === "top" ? height : 0
            const topOrBottomRadius = option.type === "top" ? topRadius : bottomRadius
            const xScale = (length / 2 - topOrBottomRadius - distanceFromBorder) / (length / 2)
            const zScale = (width / 2 - topOrBottomRadius - distanceFromBorder) / (width / 2)
            return loopPointsBase.map((point) => ({x: point.x * xScale, y, z: point.z * zScale}))
        }
        throw new Error("Only side type supported for now")
    })().map((point) => {
        const uv = getCanonicalUVForPoint([point.x, point.y, point.z])
        return {corner: true, sectionBreak: false, uv, uvSpace: "global"}
    })

    return controlPoints
}
